import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FolderOpen, DollarSign, Users, TrendingDown } from 'lucide-react';
import { getProjects } from '../../services/adminDealFlowApi';
import { Card, CardContent } from '../../components/new-components/ui/card';
import { Badge } from '../../components/new-components/ui/badge';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getProjects();
        setProjects(res.data.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-display font-semibold text-[#242424]">Projects</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage investment projects and distributions</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((proj) => (
          <Link key={proj.id} to={`/admin/projects/${proj.id}`}>
            <Card className="border-border hover:border-foreground/30 transition cursor-pointer h-full">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-[#F0F0F0] rounded-lg flex items-center justify-center border border-border">
                    <FolderOpen className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <Badge variant={proj.status === 'open' ? 'default' : 'secondary'} className="text-xs">
                    {proj.status === 'open' ? 'Active' : proj.status}
                  </Badge>
                </div>
                <h3 className="text-lg font-semibold text-[#242424] mb-4 break-words">{proj.title}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="text-center p-2 bg-[#F0F0F0] rounded">
                    <DollarSign className="h-4 w-4 text-[hsl(var(--chart-2))] mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">Capital</p>
                    <p className="text-sm font-semibold text-[#242424]">{formatCurrency(proj.total_capital)}</p>
                  </div>
                  <div className="text-center p-2 bg-[#F0F0F0] rounded">
                    <TrendingDown className="h-4 w-4 text-[hsl(var(--success))] mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">Distributions</p>
                    <p className="text-sm font-semibold text-[#242424]">{formatCurrency(proj.total_distributions)}</p>
                  </div>
                  <div className="text-center p-2 bg-[#F0F0F0] rounded">
                    <Users className="h-4 w-4 text-[hsl(var(--chart-2))] mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">Stakeholders</p>
                    <p className="text-sm font-semibold text-[#242424]">{proj.stakeholder_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}

        {projects.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            No projects yet. Create a capital raise and fund it to see projects here.
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
